import * as genericService from "./genericFormServices";
import type { CreateAttachmentDto } from "../types/MainType.type";
import { formatSignatureUrl, type FetchParams } from "./NetworkService";
import type { LegalAdviceFormThreeData } from "../types/LegalAdvices/LegaladviceFormThree.type";

const ENDPOINT = "legal-advice-form-three";

export interface LegalAdviceFormThreeFetchResponse {
  data: LegalAdviceFormThreeData[];
  totalItems: number;
}

// ---------------------------------------------------------------------------
// 1. Mapper for Legal Advice Form Three
// ---------------------------------------------------------------------------
export const mapLegalAdviceFormThreeBackendToFrontend = (
  item: any
): LegalAdviceFormThreeData => {
  if (!item) return {} as LegalAdviceFormThreeData;

  const form = item.legalAdviceFormThree || item.formData || item;

  return {
    id: item.id || form.id || "",
    documentNumber: item.documentNumber || "",
    formType: item.formType || 'LEGAL_ADVICE_FORM_THREE',
    status: item.status || form.status || 'DRAFT',

    // Form-Specific Fields
    regNo: form.regNo || "",
    regSerialNumber: form.regSerialNumber || "",
    courtName: form.courtName || "",
    caseNumber: form.caseNumber || "",
    plaintiffName: form.plaintiffName || "",
    defendantNameAndNrc: form.defendantNameAndNrc || "",
    defendantFatherName: form.defendantFatherName || "",
    defendantAddress: form.defendantAddress || "",
    policeStationAndFirstReportNo: form.policeStationAndFirstReportNo || "",
    briefFacts: form.briefFacts || "",
    chargeSectionAndDate: form.chargeSectionAndDate || "",
    judgmentSentenceAndDate: form.judgmentSentenceAndDate || "",
    appealedSectionAndDate: form.appealedSectionAndDate || "",
    appellateDecisionAndDate: form.appellateDecisionAndDate || "",
    acquittalGrounds: form.acquittalGrounds || "",
    judgmentCorrectness: form.judgmentCorrectness || "",
    revisionRequired: form.revisionRequired || "",
    reasons: form.reasons || "",

    // Signer 1: Prosecutor
    prosecutorName: form.prosecutorName || "",
    prosecutorSignatureImage: formatSignatureUrl(form.prosecutorSignatureImage),
    prosecutorSubmissionDate: form.prosecutorSubmissionDate || null,

    // Signer 2: Responsible Officer
    responsibleOfficerOpinion: form.responsibleOfficerOpinion || "",
    officerName: form.officerName || "",
    officerOffice: form.officerOffice || "",
    officerSignatureImage: formatSignatureUrl(form.officerSignatureImage),
    officerSubmissionDate: form.officerSubmissionDate || null,

    // Geo Levels (FIXED: Added targetGeoLevels and originGeoLevel)
    originGeoLevel: item.originGeoLevel || "",
    targetGeoLevels: Array.isArray(item.targetGeoLevels) ? item.targetGeoLevels : [],

    // Attachments Array
    attachments: item.attachments || form.attachments || [],


    // Dates & Status Metadata
    rejectionReason: item.rejectionReason || form.rejectionReason || '',
    submitDate: form.submitDate || item.signedAt || item.updatedAt || '',
    createdAt: item.createdAt || form.createdAt || '',
    updatedAt: item.updatedAt || form.updatedAt || '',

    // Relational Metadata (User, Office, Logs)
    // Relational Metadata
    createdBy: item.createdBy
      ? {
          id: item.createdBy.id,
          name: item.createdBy.name,
          email: item.createdBy.email,
          role: item.createdBy.role,
          position: item.createdBy.position,
        }
      : undefined,
    originOffice: item.originOffice
      ? {
          id: item.originOffice.id,
          name: item.originOffice.name,
          code: item.originOffice.code,
        }
      : undefined,
    stageHistory: Array.isArray(item.stageHistory)
  ? item.stageHistory.map((history: any) => ({
      id: history.id,
      action: history.action,
      comments: history.comments,
      attachments: history.attachments,
      createdAt: history.createdAt,
      performedBy: history.performedBy
        ? {
            id: history.performedBy.id,
            name: history.performedBy.name,
            role: history.performedBy.role,
            position: history.performedBy.position,
            office: history.performedBy.office
              ? {
                  id: history.performedBy.office.id,
                  name: history.performedBy.office.name,
                  code: history.performedBy.office.code,
                  level: history.performedBy.office.level,
                }
              : undefined,
          }
        : undefined,
    }))
  : [],
    auditLogs: Array.isArray(item.auditLogs) ? item.auditLogs : [],
  };
};

// ---------------------------------------------------------------------------
// 2. Custom Filter Function for Legal Advice Form Three
// ---------------------------------------------------------------------------
const legalAdviceFormThreeFilterPredicate = (
  app: LegalAdviceFormThreeData,
  params: FetchParams
): boolean => {
  const { searchTerm, selectedStatus, selectedDate } = params;

  const matchesStatus = !selectedStatus || app.status === selectedStatus;
  const matchesDate =
    !selectedDate ||
    Boolean(app.submitDate && app.submitDate.startsWith(selectedDate));

  const term = (searchTerm || "").toLowerCase().trim();

  const matchesGeneralSearch =
    !term ||
    Boolean(app.caseNumber && app.caseNumber.toLowerCase().includes(term)) ||
    Boolean(app.courtName && app.courtName.toLowerCase().includes(term)) ||
    Boolean(app.plaintiffName && app.plaintiffName.toLowerCase().includes(term)) ||
    Boolean(
      app.defendantNameAndNrc &&
        app.defendantNameAndNrc.toLowerCase().includes(term)
    ) ||
    Boolean(
      app.documentNumber && app.documentNumber.toLowerCase().includes(term)
    );

  return Boolean(matchesStatus && matchesDate && matchesGeneralSearch);
};

// ---------------------------------------------------------------------------
// 3. API Request Functions (Office User - Form Endpoint)
// ---------------------------------------------------------------------------

// Fetch All / Filtered
export const fetchLegalAdviceFormThreeApplications = (
  params: FetchParams
): Promise<LegalAdviceFormThreeFetchResponse> =>
  genericService.fetchApplications<LegalAdviceFormThreeData>(
    ENDPOINT,
    params,
    mapLegalAdviceFormThreeBackendToFrontend,
    legalAdviceFormThreeFilterPredicate
  );

// Fetch Single Application by ID
export const fetchLegalAdviceFormThreeById = (
  id: string
): Promise<LegalAdviceFormThreeData> =>
  genericService.fetchApplicationById<LegalAdviceFormThreeData>(
    ENDPOINT,
    id,
    mapLegalAdviceFormThreeBackendToFrontend
  );

// Save Draft (Create POST or Update PUT)
export const saveLegalAdviceFormThreeDraft = (
  id: string | null,
  data: Partial<LegalAdviceFormThreeData>
) => genericService.saveDraft<LegalAdviceFormThreeData>(ENDPOINT, id, data);

// Submit Form (Includes Signatures inside DTO)
export const submitLegalAdviceFormThreeForm = (
  id: string,
  data: LegalAdviceFormThreeData
) => genericService.submitForm<LegalAdviceFormThreeData>(ENDPOINT, id, data);

// Delete Form (Soft Delete)
export const deleteLegalAdviceFormThreeForm = (id: string) =>
  genericService.deleteForm(ENDPOINT, id);

// ---------------------------------------------------------------------------
// 4. Superior Stage Actions (Global DocumentController)
// ---------------------------------------------------------------------------
export const startReviewLegalAdviceForm = (id: string) =>
  genericService.startReviewForm(id);

// Approve Form (PATCH /documents/:id/approve)
// export const approveLegalAdviceFormThreeForm = (id: string, remark?: string) =>
//   genericService.attachForm(id, remark);

export const uploadFileService = (
  file: File
) => genericService.uploadFile(file);

export const attachLegalAdviceForm = (
  id: string,
  comments?: string,
  attachments?: CreateAttachmentDto[],
  targetGeoLevels?: string[]
) => genericService.attachForm(id, comments, attachments, targetGeoLevels);


// // Return Form for Revisions (PATCH /documents/:id/return)
// export const returnLegalAdviceFormThreeForm = (id: string, remark?: string) =>
//   genericService.returnForm(id, remark);

// // Reject Form (PATCH /documents/:id/reject)
// export const rejectLegalAdviceFormThreeForm = (id: string, remark?: string) =>
//   genericService.rejectForm(id, remark);